import { useMemo } from "react";
import { useAuth } from "@/contexts/auth-context";
import { usePermissions } from "@/contexts/permission-context";
import type { RoutePermissions } from "@/routes/config";

/**
 * Route guard permission checker.
 * Fully synchronous — no API calls, no loading states.
 * All data from AuthContext + PermissionContext.
 */
export const usePermissionChecker = () => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const { isAdmin, hasPermission: hasCrudPermission } = usePermissions();

  const hasPermission = useMemo(() => {
    return (permissions: RoutePermissions): boolean => {
      if (permissions.access === "public") return true;
      if (!isAuthenticated || !user) return false;
      if (permissions.access === "authenticated") return true;
      if (permissions.access === "admin") return isAdmin;
      if (permissions.access === "permission") {
        if (!permissions.entity || !permissions.action) return false;
        return hasCrudPermission(permissions.entity, permissions.action);
      }
      if (permissions.access === "custom") {
        if (!permissions.customCheck) return false;
        return permissions.customCheck(user);
      }
      return false;
    };
  }, [user, isAuthenticated, isAdmin, hasCrudPermission]);

  return { user, isAdmin, isAuthenticated, isLoading, hasPermission };
};
